import { useEffect, useRef, useState } from 'react'
import PlanPreview from './PlanPreview'
import PlanUpload from './PlanUpload'
import { errorMessage } from '../lib/api'
import { useUpdatePark } from '../lib/hooks'
import type { MapConfig, ParkDetail } from '../lib/types'

interface Draft {
  name: string
  locale: string
  currency: string
  width: string
  height: string
  image: string
  tiles: string
  tileMaxZoom: string
  minZoom: string
  maxZoom: string
  background: string
}

const str = (value: string | number | null | undefined) => (value === null || value === undefined ? '' : String(value))

function toDraft(detail: ParkDetail): Draft {
  return {
    name: detail.park.name,
    locale: detail.park.locale,
    currency: detail.park.currency,
    width: str(detail.map?.width),
    height: str(detail.map?.height),
    image: str(detail.map?.image),
    tiles: str(detail.map?.tiles),
    tileMaxZoom: str(detail.map?.tileMaxZoom),
    minZoom: str(detail.map?.minZoom),
    maxZoom: str(detail.map?.maxZoom),
    background: str(detail.map?.background),
  }
}

const text = (value: string) => (value.trim() === '' ? undefined : value.trim())
const num = (value: string) => {
  const parsed = Number(value.trim().replace(',', '.'))
  return value.trim() === '' || !Number.isFinite(parsed) ? undefined : parsed
}

export default function MapTab({ slug, detail }: { slug: string; detail: ParkDetail }) {
  const [draft, setDraft] = useState<Draft>(() => toDraft(detail))
  const [saved, setSaved] = useState(false)
  const save = useUpdatePark(slug)

  // Een upload verandert de afmetingen en het beeldpad. Het formulier draagt dan nog de waarden van
  // voor die upload, en dat zijn precies de waarden die bij "Opslaan" de nieuwe weer zouden overschrijven.
  const gezien = useRef(detail.mapRevision)
  useEffect(() => {
    if (gezien.current !== detail.mapRevision) {
      gezien.current = detail.mapRevision
      setDraft(toDraft(detail))
      setSaved(false)
    }
  }, [detail])

  const set = <K extends keyof Draft>(key: K, value: string) => {
    setSaved(false)
    setDraft((current) => ({ ...current, [key]: value }))
  }

  const width = num(draft.width)
  const height = num(draft.height)
  const sizeOk = width !== undefined && height !== undefined && width > 0 && height > 0

  const submit = () => {
    if (!sizeOk) return
    // The map object is replaced as a whole, so keep whatever the API stored that this form does not
    // show (georeference, for instance) and only overwrite the fields below.
    const map: MapConfig = { ...detail.map, width, height }
    map.image = text(draft.image)
    map.tiles = text(draft.tiles)
    map.tileMaxZoom = num(draft.tileMaxZoom)
    map.minZoom = num(draft.minZoom)
    map.maxZoom = num(draft.maxZoom)
    map.background = text(draft.background)
    save.mutate(
      { name: draft.name.trim(), locale: draft.locale.trim(), currency: draft.currency.trim(), map, mapRevision: detail.mapRevision },
      { onSuccess: () => setSaved(true) },
    )
  }

  return (
    <>
      <PlanUpload detail={detail} />

      <form
        className="card"
        onSubmit={(event) => {
          event.preventDefault()
          submit()
        }}
      >
        <h2>Park en plattegrond</h2>
        <div className="grid cols-3">
          <div className="field">
            <label htmlFor="park-name">Naam</label>
            <input id="park-name" type="text" value={draft.name} onChange={(e) => set('name', e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="park-locale">Taal</label>
            <input id="park-locale" type="text" value={draft.locale} onChange={(e) => set('locale', e.target.value)} />
            <span className="help">Bijvoorbeeld nl of nl_NL</span>
          </div>
          <div className="field">
            <label htmlFor="park-currency">Valuta</label>
            <input
              id="park-currency"
              type="text"
              value={draft.currency}
              onChange={(e) => set('currency', e.target.value)}
            />
            <span className="help">ISO-code, bijvoorbeeld EUR</span>
          </div>
        </div>

        <div className="grid cols-4" style={{ marginTop: '1rem' }}>
          <div className="field">
            <label htmlFor="map-width">Breedte (px)</label>
            <input id="map-width" type="number" min={1} value={draft.width} onChange={(e) => set('width', e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="map-height">Hoogte (px)</label>
            <input
              id="map-height"
              type="number"
              min={1}
              value={draft.height}
              onChange={(e) => set('height', e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="map-min-zoom">Min. zoom</label>
            <input id="map-min-zoom" type="number" value={draft.minZoom} onChange={(e) => set('minZoom', e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="map-max-zoom">Max. zoom</label>
            <input id="map-max-zoom" type="number" value={draft.maxZoom} onChange={(e) => set('maxZoom', e.target.value)} />
          </div>
        </div>

        <div className="grid cols-2" style={{ marginTop: '1rem' }}>
          <div className="field">
            <label htmlFor="map-image">Afbeelding (URL)</label>
            <input id="map-image" type="text" value={draft.image} onChange={(e) => set('image', e.target.value)} />
            <span className="help">Eén afbeelding voor kleine plattegronden. Leeg laten bij tegels.</span>
          </div>
          <div className="field">
            <label htmlFor="map-tiles">Tegels (URL-sjabloon)</label>
            <input id="map-tiles" type="text" value={draft.tiles} onChange={(e) => set('tiles', e.target.value)} />
            <span className="help">Bijvoorbeeld /parks/demo/tiles/&#123;z&#125;/&#123;x&#125;/&#123;y&#125;.png</span>
          </div>
          <div className="field">
            <label htmlFor="map-tile-zoom">Max. tegelzoom</label>
            <input
              id="map-tile-zoom"
              type="number"
              value={draft.tileMaxZoom}
              onChange={(e) => set('tileMaxZoom', e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="map-background">Achtergrondkleur</label>
            <input
              id="map-background"
              type="text"
              value={draft.background}
              onChange={(e) => set('background', e.target.value)}
            />
            <span className="help">CSS-kleur achter de tekening, bijvoorbeeld #e9efe4</span>
          </div>
        </div>

        <div className="actions">
          <button type="submit" className="btn primary" disabled={!sizeOk || save.isPending}>
            {save.isPending ? 'Opslaan…' : 'Opslaan'}
          </button>
          {!sizeOk && <span className="inline-error">Breedte en hoogte zijn verplicht en groter dan nul.</span>}
          {save.isError && <span className="inline-error">{errorMessage(save.error)}</span>}
          {saved && !save.isPending && !save.isError && <span className="inline-ok">Opgeslagen</span>}
        </div>
      </form>

      <div className="card">
        <h2>Controle: ligt de tekening op de plaatsen?</h2>
        <PlanPreview detail={detail} />
      </div>
    </>
  )
}
